import mixin from '../privates/utils/mixin';
import FormElement from './FormElement';
import StyleMixin from './mixins/styleMixins/StyleMixin';
import DisabledMixin from './mixins/DisabledMixin';
import HiddenCollapsedMixin from './mixins/HiddenCollapsedMixin';
import FocusMixin from './mixins/FocusMixin';
import ClickableMixin from './mixins/ClickableMixin';
import {validate} from '../privates/validations/validation';
import * as members from '../privates/privateMembers';
import valueSanitzationAlgorithm from '../privates/utils/valueSanitzationAlgorithm';
import RequiredMixin from './mixins/RequiredMixin';
import CheckedMixin from './mixins/CheckedMixin';


/**
 * @class Checkbox
 * @summary Checkboxes are used for a single binary choice.
 * @mixes $w.FormElement
 * @mixes $w.DisabledMixin
 * @mixes $w.HiddenCollapsedMixin
 * @mixes $w.FocusMixin
 * @mixes $w.ClickableMixin
 * @mixes $w.StyleMixin
 * @mixes $w.RequiredMixin
 * @mixes $w.CheckedMixin
 * @memberof $w
 * @queryable
 * @tagname checkbox
 * @viewername wysiwyg.viewer.components.inputs.Checkbox
 * @AppController wysiwyg.viewer.components.inputs.Checkbox
 */
class Checkbox extends FormElement {

    /**
     * @member value
     * @label property
     * @syntax
     * get value(): string
     * set value(value: string): void
     * @summary Sets or gets a checkbox's value.
     * @description
     *  The `value` property is not related to whether the checkbox is checked or
     *  not. To determine the checked status of a checkbox, use the [`checked`](#checked)
     *  property.
     *
     *  The `value` property is used for storing a value that is associated with
     *  the checkbox.
     *
     *  It is not considered when evaluating the checkbox's validity
     *  and it does not trigger an `onChange` event when the value is modified.
     *
     *  Even if a checkbox is connected to a dataset, its `value` property
     *  is not related to the value of the collection field it is connected to.
     *  The field's value is determined by the checkbox's [`checked`](#checked) property.
     * 
     *  > **Note:** Changing a checkbox's `value` in code does not trigger an `onChange` event.
     * @snippet [FormElement-value_get.es6=Get an element's value]
     * @snippet [FormElement-value_set.es6=Set an element's value]
     * @type {external:String}
     * @memberof $w.Checkbox
     * @override
     */
    get value() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        return RMI.getData(compId).value;
    }

  /**
   * @member value
   * @memberof $w.Checkbox
   * @param {external:String} val
   */
    set value(val) {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const compData = RMI.getFullDataWithOverrides(compId);
        const compType = members._type.get(this).compType;
        const value = valueSanitzationAlgorithm(compData, val);
        const isValid = validate({
            compType,
            functionName: 'value',
            dataToValidate: {value},
            compName: this.id
        });

        if (!isValid) {
            return;
        }

        RMI.setData(compId, { value });
    }

    toJSON() {
        const {id, type, global, rendered, required, value, checked} = this;
        return {id, type, global, rendered, required, value, checked};
    }
}

// THE FOLLOWING JSDOC SNIPPETS ARE NOT ATTACHED TO ACTUAL CODE BUT WILL HELP TO MAKE
//  THESE CHECKBOX METHODS MORE UNDERSTANDABLE, AS THEY FUNCTION DIFFERENTLY FOR CHECKBOXES:

/**
 * @member checked
 * @label property
 * @syntax
 * get checked(): boolean
 * set checked(value: boolean): void
 * @summary Sets or gets whether a checkbox is checked.
 * @description
 *  Setting the `checked` property to `true` places a check in
 *  the checkbox. Setting it to `false` removes the check from the checkbox.
 *
 *  Getting the `checked` property returns
 *  whether the checkbox is checked or unchecked.
 * @snippet [Checkbox-checked_get.es6=Get whether a checkbox is checked]
 * @snippet [Checkbox-checked_set.es6=Set a checkbox to be checked]
 * @snippet [Checkbox-checked_toggle.es6=Toggle whether a checkbox is checked]
 * @type {external:Boolean}
 * @memberof $w.Checkbox
 * @override
 */

/**
 * @member checked
 * @memberof $w.Checkbox
 * @param {external:Boolean} val
 */


/**
 * @member required
 * @label property
 * @syntax
   * get required(): boolean
   * set required(value: boolean): void
 * @summary Determines if a checkbox is required to be checked.
 * @description
 *  If `required` is `true`, the checkbox is only valid if it is
 *  [checked](#checked).
 *
 *  If `required` is `false`, the checkbox is always valid.
 *
 *  You can also set a checkbox to be required
 *  by using the element's Settings pane in the Editor.
 * @snippet [Checkbox-required_get.es6=Get whether a checkbox is required]
 * @snippet [Checkbox-required_set.es6=Set a checkbox to be required]
 * @type {external:Boolean}
 * @memberof $w.Checkbox
 * @override
 */

/**
 * @member required
 * @memberof $w.Checkbox
 * @param {external:Boolean} val
 */


 /**
  * @member style
  * @label property
  * @syntax
  * get style(): Style
  * @summary Gets an object containing information about the checkbox's styles.
  * @description
  *  The following styles can be used with checkboxes:
  *
  *  + [`backgroundColor`]($w.Style.html#backgroundColor)
  *  + [`borderColor`]($w.Style.html#borderColor)
  *  + [`borderRadius`]($w.Style.html#borderRadius)
  *  + [`borderWidth`]($w.Style.html#borderWidth)
  *
  *
  *  Getting or setting a checkbox's styles, gets or sets the styles of the
  *  checkbox's regular state. It does not set the styles of the checkbox's hover,
  *  focus, error, or disabled states.
  * @snippet [BackgroundColorMixin-backgroundColor_set.es6=Set the background color]
  * @snippet [BackgroundColorMixin-backgroundColor_get.es6=Get the background color]
  * @type {$w.Style}
  * @memberof $w.Checkbox
  * @readonly
  * @override
  */

mixin(Checkbox.prototype, DisabledMixin.prototype);
mixin(Checkbox.prototype, HiddenCollapsedMixin.prototype);
mixin(Checkbox.prototype, FocusMixin.prototype);
mixin(Checkbox.prototype, ClickableMixin.prototype);
mixin(Checkbox.prototype, StyleMixin.prototype);
mixin(Checkbox.prototype, RequiredMixin.prototype);
mixin(Checkbox.prototype, CheckedMixin.prototype);

export default Checkbox;
